import React, { useEffect, useMemo, useState } from 'react';
import './App.css';
import { getB24, BitrixProvider, useBitrix } from './context/BitrixContext';

type OwenDevice = {
  id: number | string;
  name?: string;
  identifier?: string;
  type?: string;
  time_zone?: string;
  is_favourite?: boolean;
  is_online?: boolean;
  has_alarm?: boolean;
  has_unread?: boolean;
  status?: string;
  categories?: Array<string | number>;
  [key: string]: unknown;
};

type OwenResponse = {
  mode?: string;
  field?: string;
  object?: Record<string, unknown> | null;
  available_objects?: Array<Record<string, unknown>>;
  devices?: OwenDevice[];
  devices_error?: string | null;
};

const OWEN_FIELD_FALLBACK = 'UF_CRM_1777219814';

const statusLabel = (device: OwenDevice) => {
  if (device.has_alarm) return 'alarm';
  if (device.is_online) return 'online';
  return device.status || 'offline';
};

const statusTone = (device: OwenDevice) => {
  const status = statusLabel(device);
  if (status === 'online') return 'success';
  if (status === 'alarm' || status === 'unreadalarm') return 'danger';
  return 'muted';
};

type AlarmTestResult = {
  status: string;
  monitor_initialized: boolean;
  baseline_size: number;
  active_alarms_now: number;
  would_fire_count: number;
  would_fire: Array<Record<string, unknown>>;
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'https://owen-api.tmg-din-reestr.ru';

const AppBody = () => {
  const { loading, error, backendResponse, backendLoading, backendError, dealId } = useBitrix();
  const [editing, setEditing] = useState(false);
  const [savingDeviceId, setSavingDeviceId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [alarmTestLoading, setAlarmTestLoading] = useState(false);
  const [alarmTestResult, setAlarmTestResult] = useState<AlarmTestResult | null>(null);
  const [alarmTestError, setAlarmTestError] = useState<string | null>(null);

  const handleAlarmTest = async () => {
    setAlarmTestLoading(true);
    setAlarmTestResult(null);
    setAlarmTestError(null);
    try {
      // First: fire a real synthetic alarm (triggers print + file log on backend)
      const fireRes = await fetch(`${API_BASE_URL}/api/alarm/test-fire`, { method: 'POST' });
      if (!fireRes.ok) {
        const d = await fireRes.json().catch(() => null) as { detail?: string } | null;
        throw new Error(d?.detail ?? `Error ${fireRes.status}`);
      }

      // Then: check what the monitor would detect right now
      const checkRes = await fetch(`${API_BASE_URL}/api/alarm/test`, { method: 'POST' });
      const data = await checkRes.json().catch(() => null);
      if (!checkRes.ok) {
        throw new Error((data as { detail?: string } | null)?.detail ?? `Error ${checkRes.status}`);
      }
      setAlarmTestResult(data as AlarmTestResult);
    } catch (err) {
      setAlarmTestError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setAlarmTestLoading(false);
    }
  };

  const owen = (backendResponse?.owen ?? {}) as OwenResponse;
  const deal = (backendResponse?.deal ?? {}) as Record<string, unknown>;
  const fieldCode = String(owen.field || OWEN_FIELD_FALLBACK);
  const savedOwenId = String(deal[fieldCode] ?? '').trim();
  const devicesSource = owen.devices;
  const devices = useMemo(() => (Array.isArray(devicesSource) ? devicesSource : []), [devicesSource]);
  const savedObject = (owen.object ?? null) as Record<string, unknown> | null;
  const deviceParameters = useMemo(
    () =>
      Array.isArray(savedObject?.parameters)
        ? (savedObject?.parameters as Array<Record<string, unknown>>)
        : [],
    [savedObject],
  );
  const visibleParameters = useMemo(() => {
    const filtered = deviceParameters.filter((parameter) => {
      const flag = parameter.in_parameters;
      return flag === 1 || flag === '1' || flag === true;
    });
    const source = filtered.length ? filtered : deviceParameters;
    return [...source].sort((left, right) => {
      const leftOrder = Number(left.list_order ?? 0);
      const rightOrder = Number(right.list_order ?? 0);
      return leftOrder - rightOrder;
    });
  }, [deviceParameters]);

  const savedDevice = useMemo(() => {
    if (!savedOwenId) return null;
    return (
      devices.find((device) => String(device.id) === savedOwenId) ||
      devices.find((device) => String(device.identifier) === savedOwenId) ||
      null
    );
  }, [devices, savedOwenId]);
  const selectedNameSource = savedObject?.name ?? savedObject?.title ?? savedOwenId;
  const selectedName = savedDevice?.name || String(selectedNameSource || 'Selected device');
  const selectedType = savedDevice?.type || (savedObject?.type ? String(savedObject.type) : '');
  const selectedIdentifier = savedDevice?.identifier || (savedObject?.identifier ? String(savedObject.identifier) : '');
  const selectedStatus = savedDevice ? statusLabel(savedDevice) : String(savedObject?.status ?? 'saved');

  useEffect(() => {
    if (savedOwenId) {
      setEditing(false);
    }
  }, [savedOwenId]);

  const handleSelectDevice = async (device: OwenDevice) => {
    const b24 = getB24();
    if (!b24 || !b24.callMethod) {
      setActionError('Bitrix SDK is not initialized');
      return;
    }

    if (!dealId) {
      setActionError('Deal ID is missing');
      return;
    }

    setActionError(null);
    setSavingDeviceId(String(device.id));

    try {
      await b24.callMethod('crm.deal.update', {
        id: Number(dealId),
        fields: {
          [fieldCode]: String(device.id),
        },
      });

      window.location.reload();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save Owen ID';
      setActionError(message);
    } finally {
      setSavingDeviceId(null);
    }
  };

  if (loading) {
    return (
      <main className="App">
        <section className="status-card booting">
          <div className="status-eyebrow">Bitrix to FastAPI gate</div>
          <h1>Loading Bitrix session</h1>
          <p>Waiting for the Bitrix SDK and backend response.</p>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className="App">
        <section className="status-card error">
          <div className="status-eyebrow">Bitrix to FastAPI gate</div>
          <h1>Access denied</h1>
          <p>{error}</p>
          <div className="status-footnote">
            Open this app only inside Bitrix24 and make sure the backend is running.
          </div>
        </section>
      </main>
    );
  }

  if (!backendResponse) {
    return (
      <main className="App">
        <section className="status-card error">
          <div className="status-eyebrow">Bitrix to FastAPI gate</div>
          <h1>Backend test failed</h1>
          <p>{backendError || 'No backend response received.'}</p>
        </section>
      </main>
    );
  }

  const showPicker = editing || !savedOwenId;
  const devicesError = owen.devices_error ? String(owen.devices_error) : null;

  return (
    <main className="App">
      <section className="status-card ok">
        <div className="status-eyebrow">Bitrix to FastAPI gate</div>
        <h1>{savedOwenId ? 'Saved Owen device' : 'Select Owen device'}</h1>
        <p>
          {backendLoading
            ? 'Loading backend data...'
            : savedOwenId
              ? 'The deal already has an Owen device connected.'
              : 'Choose a device to save it into the deal field.'}
        </p>

        {backendError ? <div className="status-footnote error-text">{backendError}</div> : null}
        {actionError ? <div className="status-footnote error-text">{actionError}</div> : null}

        {!showPicker && savedOwenId ? (
          <div className="summary-panel">
            <div className="summary-title">Current device</div>
            <div className="device-name">{selectedName}</div>
            <div className="device-meta">
              <span className={`pill pill-${savedDevice ? statusTone(savedDevice) : 'muted'}`}>
                {selectedStatus}
              </span>
              {selectedType ? <span className="pill">{selectedType}</span> : null}
              {selectedIdentifier ? <span className="pill">ID {selectedIdentifier}</span> : null}
            </div>
            <div className="device-details">
              <div><span>Bitrix field</span><b>{fieldCode}</b></div>
              <div><span>Saved Owen ID</span><b>{savedOwenId}</b></div>
              <div><span>Online</span><b>{savedDevice ? (savedDevice.is_online ? 'yes' : 'no') : 'unknown'}</b></div>
              <div><span>Alarm</span><b>{savedDevice ? (savedDevice.has_alarm ? 'yes' : 'no') : 'unknown'}</b></div>
            </div>
            <div className="actions">
              <button type="button" className="secondary-button" onClick={() => setEditing(true)}>
                Change Owen ID
              </button>
            </div>
          </div>
        ) : null}

        {!showPicker && savedOwenId ? (
          <div className="picker-panel" style={{ marginTop: 16 }}>
            <div className="summary-title">Last parameter values</div>
            {visibleParameters.length ? (
              <div className="parameter-list">
                {visibleParameters.map((parameter) => {
                  const measurement = parameter.measurement as Record<string, unknown> | undefined;
                  const lastValue = parameter.value ?? parameter.formatted_value ?? null;
                  return (
                    <div className="parameter-card" key={String(parameter.id ?? Math.random())}>
                      <div className="parameter-head">
                        <div>
                          <div className="parameter-id">{String(parameter.name ?? `Parameter ${parameter.id ?? 'unknown'}`)}</div>
                          <div className="parameter-code">
                            {parameter.code ? String(parameter.code) : `ID ${String(parameter.id ?? 'unknown')}`}
                          </div>
                        </div>
                        <div className="parameter-value">{String(lastValue ?? '—')}</div>
                      </div>
                      <div className="parameter-meta">
                        {measurement?.name ? <span>{String(measurement.name)}</span> : null}
                        {measurement?.title ? <span>{String(measurement.title)}</span> : null}
                        {parameter.fault !== undefined ? <span>Fault {String(parameter.fault)}</span> : null}
                        {parameter.list_order !== undefined ? <span>Order {String(parameter.list_order)}</span> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">No parameter values returned by Owen Cloud.</div>
            )}
          </div>
        ) : null}

        {showPicker ? (
          <div className="picker-panel">
            <div className="summary-title">
              {savedOwenId ? 'Choose a new device' : 'Available devices'}
            </div>
            {devicesError ? <div className="status-footnote error-text">{devicesError}</div> : null}
            {devices.length ? (
              <div className="device-grid">
                {devices.map((device) => {
                  const key = String(device.id);
                  const isSaving = savingDeviceId === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      className="device-button"
                      onClick={() => handleSelectDevice(device)}
                      disabled={Boolean(savingDeviceId)}
                    >
                      <div className="device-button-top">
                        <div className="device-button-name">{device.name || `Device ${key}`}</div>
                        <span className={`pill pill-${statusTone(device)}`}>{statusLabel(device)}</span>
                      </div>
                      <div className="device-button-meta">
                        {device.type ? <span>{device.type}</span> : null}
                        {device.identifier ? <span>Identifier {device.identifier}</span> : null}
                        {device.time_zone ? <span>{device.time_zone}</span> : null}
                      </div>
                      <div className="device-button-footer">
                        <span>Owen ID: {key}</span>
                        {isSaving ? <span>Saving...</span> : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">No devices returned by Owen Cloud.</div>
            )}
            {savedOwenId ? (
              <div className="actions">
                <button type="button" className="secondary-button" onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="alarm-test-panel">
          <div className="summary-title">Тест мониторинга аварий</div>
          <p className="alarm-test-desc">
            Отправляет синтетическую аварию в лог (проверяет print + файл), затем показывает состояние мониторинга.
          </p>
          <div className="actions">
            <button
              type="button"
              className="secondary-button"
              onClick={handleAlarmTest}
              disabled={alarmTestLoading}
            >
              {alarmTestLoading ? 'Проверка...' : 'Тестовая авария'}
            </button>
          </div>
          {alarmTestError ? (
            <div className="status-footnote error-text">{alarmTestError}</div>
          ) : null}
          {alarmTestResult ? (
            <div className="alarm-test-result">
              <div className="alarm-test-row">
                <span>Baseline (известных аварий)</span>
                <b>{alarmTestResult.baseline_size}</b>
              </div>
              <div className="alarm-test-row">
                <span>Активных сейчас в Owen</span>
                <b>{alarmTestResult.active_alarms_now}</b>
              </div>
              <div className="alarm-test-row">
                <span>Новых (сработало бы)</span>
                <b className={alarmTestResult.would_fire_count > 0 ? 'alarm-fire' : ''}>{alarmTestResult.would_fire_count}</b>
              </div>
              {alarmTestResult.would_fire.length > 0 ? (
                <div className="alarm-fire-list">
                  {alarmTestResult.would_fire.map((entry, i) => (
                    <div className="alarm-fire-item" key={i}>
                      <div className="alarm-fire-msg">{String(entry.message ?? '—')}</div>
                      <div className="alarm-fire-meta">
                        device_id: {String(entry.device_id ?? '—')} &nbsp;·&nbsp;
                        log_id: {String(entry.last_log_id ?? '—')} &nbsp;·&nbsp;
                        {entry.is_critical ? <span className="pill pill-danger">critical</span> : <span className="pill">non-critical</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">Новых аварий нет — всё в baseline.</div>
              )}
            </div>
          ) : null}
        </div>
      </section>
    </main>
  );
};

export default function App() {
  return (
    <BitrixProvider>
      <AppBody />
    </BitrixProvider>
  );
}
